<template>
    <div
        class="hidden lg:flex w-full flex-col items-start rounded-lg bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700"
        :style="{ height: dynamicTableHeight }"
    >
        <!-- Header row -->
        <div
            class="flex items-center flex-shrink-0 self-stretch bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 rounded-t-lg"
            style="gap: -1px"
        >
            <div
                class="flex py-3 items-center gap-3 flex-1"
                :class="currentTab === 'current-rates' ? 'px-6' : 'px-5'"
            >
                <span class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-tight">
                    {{ isFlexibleCurrency ? 'Transfer to' : 'Transfer from' }}
                </span>
                <Icon icon="arrow-down" class="w-4 h-4 text-slate-400" />
            </div>

            <div
                class="flex py-3 items-center justify-start flex-1"
                :class="currentTab === 'current-rates' ? 'px-4' : 'px-3'"
            >
                <span class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-tight">Transfer rate</span>
            </div>

            <div v-if="currentTab === 'current-rates'" class="flex py-3 px-4 items-center justify-start flex-1">
                <span class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-tight">Transfer time</span>
            </div>

            <div v-if="currentTab === 'transfer-bonus-history'" class="flex py-3 px-4 items-center justify-start flex-1">
                <span class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-tight">End date</span>
            </div>

            <div v-if="currentTab === 'current-rates'" class="flex py-3 px-4 items-center justify-start w-[64px]"></div>
        </div>

        <!-- Rows -->
        <div class="flex flex-col items-start self-stretch flex-1 overflow-y-auto scrollbar-overlay">
            <div
                v-for="(partner, index) in tableRows"
                :key="partner.bonus_id || partner.destination_program_id + '-' + (partner.bonus_end_date || index)"
                :class="[
                    'flex h-[72px] items-center self-stretch hover:bg-slate-25 dark:hover:bg-slate-700/50 transition-colors duration-200',
                    index < tableRows.length - 1 ? 'border-b border-slate-100 dark:border-slate-700' : '',
                ]"
                style="gap: -1px"
            >
                <!-- Program info -->
                <div class="flex px-6 py-2.5 items-center gap-3 flex-1">
                    <div class="flex w-[84px] h-[36px] justify-center items-center flex-shrink-0">
                        <img
                            v-if="partner.destination_program_image"
                            :src="partnerImageUrl(partner)"
                            :alt="partner.destination_program_name"
                            class="max-w-full max-h-full object-contain"
                        />
                        <span v-else class="text-xs text-slate-500 dark:text-slate-400 font-medium flex items-center justify-center w-full h-full">
                            {{ partner.destination_program_name.substring(0, 2).toUpperCase() }}
                        </span>
                    </div>
                    <div class="flex flex-col items-start gap-1">
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">{{ partner.destination_program_name }}</span>
                        <span class="text-slate-500 dark:text-slate-400 text-xs font-normal leading-4">{{ formatProgramType(partner.destination_program_type) }}</span>
                    </div>
                </div>

                <!-- Transfer rate -->
                <div class="flex px-4 py-2.5 items-center justify-start gap-2 flex-1">
                    <template v-if="partner.bonus_multiplier && partner.bonus_multiplier > 1">
                        <span class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 line-through">{{ partner.base_transfer_ratio }}</span>
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">{{ partner.effective_transfer_ratio }}</span>
                        <span class="inline-flex py-[2px] px-3 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 text-sm font-medium leading-5 text-center">
                            +{{ partner.bonus_percentage }}%<template v-if="partner.bonus_end_date"> until {{ formatDateShort(partner.bonus_end_date) }}</template>
                        </span>
                    </template>
                    <template v-else>
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5">{{ partner.transfer_ratio }}</span>
                    </template>
                    <TransferRateBreakdown
                        v-if="currentTab === 'current-rates'"
                        :rates="partner.rates"
                    />
                </div>

                <!-- Transfer time (current-rates) -->
                <div v-if="currentTab === 'current-rates'" class="flex px-4 py-2.5 items-center justify-start gap-2 flex-1">
                    <Icon
                        :icon="isImmediateTransfer(partner.transfer_time) ? 'clock-fast' : 'clock'"
                        class="w-5 h-5 text-slate-800 dark:text-slate-100"
                    />
                    <span class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5">
                        {{ formatTransferTimeText(partner.transfer_time) }}
                    </span>
                </div>

                <!-- End date (history) -->
                <div v-if="currentTab === 'transfer-bonus-history'" class="flex px-4 py-2.5 items-center justify-start flex-1">
                    <span class="text-slate-600 dark:text-slate-300 text-sm font-normal leading-5">{{ formatDate(partner.bonus_end_date) }}</span>
                </div>

                <!-- Calculator button (current-rates) -->
                <div v-if="currentTab === 'current-rates'" class="flex px-4 py-2.5 items-center justify-start">
                    <button
                        @click="$emit('open-calculator', partner)"
                        @mouseenter="(e) => { partner.showCalculatorTooltip = true; partner.calculatorButtonRect = e.target.getBoundingClientRect(); }"
                        @mouseleave="partner.showCalculatorTooltip = false"
                        class="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors duration-200"
                    >
                        <Icon icon="calculator" class="w-4 h-4 text-slate-400" />
                    </button>
                    <Teleport to="body">
                        <div
                            v-if="partner.showCalculatorTooltip && partner.calculatorButtonRect"
                            class="fixed py-1 px-2 text-sm text-white bg-[#1E293B] rounded-lg shadow-lg whitespace-nowrap z-[9999] pointer-events-none -translate-x-1/2"
                            :style="{
                                top: partner.calculatorButtonRect.top - 32 + 'px',
                                left: partner.calculatorButtonRect.left + partner.calculatorButtonRect.width / 2 + 'px',
                            }"
                        >
                            Points calculator
                        </div>
                    </Teleport>
                </div>
            </div>

            <!-- Empty state -->
            <div v-if="tableRows.length === 0" class="flex flex-col justify-center self-stretch flex-1 py-16">
                <div class="text-center">
                    <Icon :icon="isAnyFilterApplied ? 'filters' : 'search'" class="w-8 h-8 mx-auto mb-2 text-slate-600 dark:text-slate-300" />
                    <p class="text-slate-500 dark:text-slate-400 text-sm leading-5 font-normal text-center">
                        <template v-if="isAnyFilterApplied">No programs found. Try adjusting your filters.</template>
                        <template v-else-if="currentTab === 'transfer-bonus-history'">No transfer bonus history exists for this program.</template>
                        <template v-else>No transfer partners found for this program.</template>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import TransferRateBreakdown from '@/components/transfer-partners/TransferRateBreakdown.vue';

const props = defineProps({
    currentTab: String,
    tableRows: Array,
    isFlexibleCurrency: Boolean,
    isAnyFilterApplied: Boolean,
    formatDate: Function,
    formatDateShort: Function,
    formatProgramType: Function,
    formatTransferTimeText: Function,
    isImmediateTransfer: Function,
});

defineEmits(['open-calculator']);

const dynamicTableHeight = computed(() => {
    const headerHeight = 60;
    const rowHeight = 72;
    const minHeight = 240;
    const maxHeight = 520;
    const rowsCount = props.tableRows.length;
    if (rowsCount === 0) return `${minHeight}px`;
    const calculatedHeight = headerHeight + rowsCount * rowHeight;
    return `${Math.min(calculatedHeight, maxHeight)}px`;
});

const cdnUrl = window.cdnUrl;

const partnerImageUrl = (partner) => {
    const image = partner.destination_program_image;
    return image ? `${cdnUrl}/${image.path}/_small/${image.file_name}` : '';
};
</script>

<style scoped>
.scrollbar-overlay {
    scrollbar-width: thin;
    scrollbar-color: rgba(148, 163, 184, 0.3) transparent;
}
.scrollbar-overlay::-webkit-scrollbar { width: 8px; }
.scrollbar-overlay::-webkit-scrollbar-track { background: transparent; }
.scrollbar-overlay::-webkit-scrollbar-thumb {
    background-color: rgba(148, 163, 184, 0.3);
    border-radius: 4px;
    border: transparent;
}
.scrollbar-overlay::-webkit-scrollbar-thumb:hover {
    background-color: rgba(148, 163, 184, 0.5);
}
</style>
